import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, type OnInit } from '@angular/core';
import { GenericRichTextDirective } from '@core/jss-utils';
import {
  CrntButton,
  CrntFormControlLabel,
  CrntFormControlValidationMessage,
  CrntIcon,
  CrntRadioGroup,
} from '@essent/current-web-angular';
import {
  getFeatureFlags,
  type OrderCrossSellFormValues,
} from '@innogy/become-a-customer/state';
import { CommonUiProductsModule } from '@innogy/common-ui/products';
import { CommonUiProgressiveFormsModule } from '@innogy/common-ui/progressive-forms';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import {
  EuroCurrencyPipe,
  HasValueDirective,
  IsInvalidPipe,
} from '@innogy/utils-deprecated';
import type { StoreProductData } from '@legacy/common-ui-models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';
import { SetValueAction } from 'ngrx-forms';

import { CrossSellLoyaltyInfoModalComponent } from '../../../modals/cross-sell-loyalty-info-modal/cross-sell-loyalty-info-modal.component';
import { CrossSellProductInfoModalComponent } from '../../../modals/cross-sell-product-info-modal/cross-sell-product-info-modal.component';
import { OrderFulfillmentStepComponent } from '../order-fulfillment-step.component';

@Component({
  selector: 'wl-order-fulfillment-cross-sell[progressiveFormState]',
  templateUrl: './order-fulfillment-cross-sell.component.html',
  styleUrls: ['./order-fulfillment-cross-sell.component.essent.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CommonUiProgressiveFormsModule,
    CommonUiProductsModule,
    CrntRadioGroup,
    CrntFormControlLabel,
    CrntButton,
    CrntFormControlValidationMessage,
    HasValueDirective,
    IsInvalidPipe,
    EuroCurrencyPipe,
    GenericRichTextDirective,
    JssModule,
    CrntIcon,
  ],
})
export class OrderFulfillmentCrossSellComponent
  extends OrderFulfillmentStepComponent<OrderCrossSellFormValues>
  implements OnInit
{
  private readonly modalService = inject(NgbModal);
  private readonly store$ = inject(Store);
  private readonly deviceInfo = inject(MediaObserverService).deviceInfo;

  readonly product = input.required<StoreProductData>();

  readonly thuisvoordeelAddonEnabled = this.store$.selectSignal(
    getFeatureFlags('crossSellThuisvoordeelAddon')
  );

  override readonly progressiveFormCardContent = computed(() => ({
    title: this.orderContent().crossSellCardTitle,
    subtitle: this.orderContent().crossSellCardSubtitle,
    submitButtonText: this.orderContent().enableCustomerTypeStep
      ? this.orderContent().customerTypeNavigateToButtonText
      : this.orderContent().crossSellCardSubmitButton,
    stepNumber: this.stepNumber(),
  }));

  readonly usps = computed(
    () =>
      this.product()
        .content.usps?.slice(0, 3)
        .map((usp) => ({ ...usp, tooltip: '' })) ?? []
  );

  // This property will always be set during initialization based on the form state and device type
  mainProductShown: boolean | undefined;

  ngOnInit() {
    this.mainProductShown =
      this.progressiveFormState().formState.value.hasSelectedProduct ||
      (this.orderContent().crossSellDesktopOnly
        ? this.deviceInfo().isDesktop
        : true);
  }

  onCardClick($event: Event) {
    $event.preventDefault();

    this.store$.dispatch(
      new SetValueAction(
        this.formState().controls.hasSelectedProduct.id,
        !this.formState().controls.hasSelectedProduct.value
      )
    );
  }

  onMoreInfoClick($event: Event) {
    $event.stopPropagation();

    this.modalService.dismissAll();
    const modal = this.modalService.open(CrossSellProductInfoModalComponent, {
      modalDialogClass: 'modal-shadow',
      animation: true,
      centered: true,
    });

    modal.componentInstance.rendering.set(this.rendering());
    modal.componentInstance.product.set(this.product());
    modal.componentInstance.formState.set(this.formState());
  }

  onLoyaltyMoreInfoClick($event: Event) {
    $event.stopPropagation();

    this.modalService.dismissAll();
    const modal = this.modalService.open(CrossSellLoyaltyInfoModalComponent, {
      modalDialogClass: 'modal-shadow',
      animation: true,
      centered: true,
    });

    modal.componentInstance.rendering.set(this.rendering());
    modal.componentInstance.formState.set(this.formState());
  }
}
